import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PortalSettingsService } from '../../../core/services/portal-settings.service';
import { AccountService } from '../services/account.service';
import { NotificationsService } from 'src/app/core/services/notifications.service';
import { UtilityService } from '../../../core/services/utility.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoginOnboardCoPickModalComponent } from '../../../modals/account/login-onboard-co-pick-modal/login-onboard-co-pick-modal.component';

enum MainMenuType {
  Default = 'default',
  Enroll = 'enroll',
  SignInHelp = 'signInHelp'
}
enum SubMenuType {
  Default = 'default',
  EmployeeLogin = 'employeeLogin',
  WindowsLogin = 'windowsLogin'
}

enum LoginType {
  Default = 'default',
  ClockInOut = 'clockInOut',
  FieldMode = 'fieldMode'
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {
  portalSettings: any;
  welcomeText: string;
  coForLogo: number;
  logoUrl: string;
  coDropdownCompanies: any;
  activeSubMenu: SubMenuType = SubMenuType.Default;
  activeMainMenu: MainMenuType = MainMenuType.Default;
  company: number;
  defaultCompany: number;
  passwordResetButtonText: string;
  signInHelpText: string;
  isMobileMode: boolean = false;
  capsLockOn: boolean = false;
  isLoading: boolean = false;
  enableEnrollAndPasswordReset: boolean = true;
  enableOnboarding: boolean = false;
  companiesEnabledForOnboarding: any = [];
  viewpointOneLoginEnabled: boolean = false;
  smsPasswordReset: boolean = false;
  setPasswordInEnrollment: boolean = false;
  securityPinText: string;
  isWindowsPinRequired: boolean = false;
  returnUrl: string = null;
  companyFromUrl: number = null;
  isCoDropdownVisible: boolean = true;
  isCompanySelection: boolean = false;
  changeLogo: boolean = false;
  vpid: string = null;
  idToken: string = null;
  currentYear: number;
  enableClockInOut: boolean = false;
  enableFieldMode: boolean = false;
  loginErrorMsg: string;
  enableViewpointOneLogin: boolean;
  hideEnrollHelp: boolean;

  // login option flags:
  isViewpointLoginEnabled: boolean = true;
  isEmployeeLoginEnabled: boolean = true;
  isWindowsLoginEnabled: boolean = true;
  isVistaTidEnabled: boolean = false;

  employeeLoginForm = new FormGroup({
    companySelect: new FormControl(null),
    companyInput: new FormControl(null),
    employeeNum: new FormControl(null, Validators.required),
    employeePassword: new FormControl(null, Validators.required)
  });

  windowsLoginForm = new FormGroup({
    windowsUsername: new FormControl(null, Validators.required),
    windowsPassword: new FormControl(null, Validators.required),
    windowsPin: new FormControl(null)
  });
 
  enrollForm = new FormGroup({
    companySelect: new FormControl(null),
    companyInput: new FormControl(null),
    employeeNum: new FormControl(null, Validators.required),
    securityPin: new FormControl(null, Validators.required),
    password: new FormControl(null),
    confirmPassword: new FormControl(null)
  });

  passwordResetForm = new FormGroup({
    companySelect: new FormControl(null),
    companyInput: new FormControl(null),
    employeeNum: new FormControl(null, Validators.required),
    securityPin: new FormControl(null, Validators.required)
  });

  @ViewChild('employeeNum') employeeLoginNum: ElementRef;
  @ViewChild('windowsUsername') windowsUsername: ElementRef;

  constructor(portalSettingsService: PortalSettingsService, private accountService: AccountService, private notificationsService: NotificationsService, private utilityService: UtilityService, private ngbModalService: NgbModal) {
    this.portalSettings = portalSettingsService.getPortalSettings();
    this.isMobileMode = this.utilityService.isMobileMode();
    this.currentYear = new Date().getFullYear();
  }

  async ngOnInit() {
    await this.getUrlParams();

    document.body.style.backgroundImage = "url('/Cache/images/trimble-background.png')";
    window.addEventListener('keyup', (event) => this.capsLockEvent(event));
    window.addEventListener('resize', (event) => this.windowResizeEvent(event));

    // Initialize portal settings:
    this.setLoginOptions();
    await this.getCompanyConfiguration();

    this.enableEnrollAndPasswordReset = this.portalSettings.DisableEnrollPasswordChange.ConfigValue == 0;
    this.enableClockInOut = this.portalSettings.ClockInLoginEnabled.ConfigValue == 1;
    this.enableFieldMode = this.portalSettings.FieldLoginEnabled.ConfigValue == 1;
    this.smsPasswordReset = this.portalSettings.SendSMSOnResetIfNoEmail.ConfigValue != 'E';
    this.setPasswordInEnrollment = this.portalSettings.PortalEnrollmentMode.ConfigValue == 'D';
    this.welcomeText = this.portalSettings.LoginWelcomeRichText.ConfigValue;
    this.passwordResetButtonText = this.portalSettings.ResetPasswordOverrideText.ConfigValue ?? 'Need sign in help?';
    this.signInHelpText = this.portalSettings.LoginPageHelpingText.ConfigValue;
    this.securityPinText = this.portalSettings.SecurityPinMessage.ConfigValue ?? 'Enter your security PIN:';
    this.isWindowsPinRequired = this.portalSettings.WindowsLoginPinReqd.ConfigValue == 1;
    this.enableViewpointOneLogin = this.portalSettings.ViewpointOneLoginEnabled.ConfigValue === "1";
    this.hideEnrollHelp = this.portalSettings.DisableEnrollPasswordChange.ConfigValue === "1";

    this.loginErrorMsg = "Login failed - please confirm your information and retry.  Note that repeated login attempts may lock your account.";
    if (this.enableViewpointOneLogin) {
      this.loginErrorMsg += " If you have previously linked your employee login to Viewpoint ID, please log in that way.";
    }
    if (!this.hideEnrollHelp) {
      this.loginErrorMsg += " If you have forgotten your password, reset your password below.";
    }
  }

  ngOnDestroy() {
    window.removeEventListener('keyup', (event) => this.capsLockEvent(event));
    window.removeEventListener('resize', (event) => this.windowResizeEvent(event));
  }

  async getUrlParams() {
    let query = window.location.search;
    let urlParams = new URLSearchParams(query);

    let url = urlParams.get('ReturnUrl');
    const returnUrlResponse = await this.accountService.checkReturnUrl(url);
    this.returnUrl = returnUrlResponse['returnUrl'];

    let urlCoString = urlParams.get('co');
    if (urlCoString != null && urlCoString != '') {
      const companyNumResponse = await this.accountService.getCompanyNumFromUrl(urlCoString);
      this.companyFromUrl = parseInt(companyNumResponse['co']);
    }

    this.vpid = urlParams.get('vpid'); 
    this.idToken = urlParams.get('idToken');
    if (this.vpid && this.idToken) {
      this.notificationsService.showWarning("You do not have a Vista Web portal account associated with this Viewpoint ID. Please login or enroll via the Vista Web portal login to associate your account.");
    }

    let timedOut = urlParams.get('timedOut');
    if(timedOut == 'True') {
      this.notificationsService.showError("You have been logged out due to inactivity.", window.location.pathname);
    }
  }

  getVistaTidEnabled() {
    this.accountService.getVistaTidEnabled().then((response: boolean) => {
      this.isVistaTidEnabled = response;
    })
  }

  async getCompanyConfiguration() {
    let isCompanyDropdown = this.portalSettings.CompanyLoginChoices.ConfigValue == 1;
    let logoCo = this.portalSettings.LogoHQCo.ConfigValue;

    // Company specific configuration
    if (this.companyFromUrl) {
      if (logoCo) {
        this.setLogoUrl(logoCo);
      }
      else {
        this.setLogoUrl(this.companyFromUrl);
      }
      this.isCompanySelection = false;
      this.isCoDropdownVisible = false;
      this.setOnboardButtonVisibility([{PRCo: this.companyFromUrl, PRCoNameMask: null}]);
      this.company = this.companyFromUrl;
    }
    // Company dropdown
    else if (isCompanyDropdown) {
      this.coDropdownCompanies = await this.getCompanies();
      this.setDefaultCompany();
      // only one company in dropdown. No need to make company selections, so hide dropdown
      if (this.coDropdownCompanies && this.coDropdownCompanies.length === 1) {
        this.isCoDropdownVisible = false;
        this.isCompanySelection = false;
      }
      // 2+ companies in dropdown
      else {
        this.isCoDropdownVisible = true;
        this.isCompanySelection = true;
      }

      if (logoCo) {
        this.setLogoUrl(logoCo);
      }
      else {
        this.setLogoUrl(this.company);
        this.changeLogo = true;
      }

      this.setOnboardButtonVisibility(this.coDropdownCompanies);
    }
    // Company input
    else {
      this.setLogoUrl(logoCo);

      this.isCoDropdownVisible = false;
      this.isCompanySelection = true;

      this.enableOnboarding = false;
    }

    this.setCompanyValidationOnForms();
  }

  getCompanies(): Promise<any[]> {
    return this.accountService.getCompanies().then((companies: any) => {
      return companies;
    }, () => { return null });
  }

  setDefaultCompany() {
    let defaultCompany = this.portalSettings.DefaultLoginCompany.ConfigValue;
    let defaultDropdownCo = this.coDropdownCompanies.find(co => co.PRCo == defaultCompany);
    if (defaultDropdownCo !== null && defaultDropdownCo !== undefined) {
      this.company = defaultDropdownCo.PRCo;
    }
    else {
      this.company = this.coDropdownCompanies[0]?.PRCo;
    }
    this.setCompanySelect(this.company);
  }

  async setOnboardButtonVisibility(availableCompanies: any[]) {
    this.enableOnboarding = this.portalSettings.EnableOnboarding.ConfigValue == 1;

    if (this.enableOnboarding && availableCompanies !== null) {
      let onboardCompanies = this.getOnboardingCompanies();
      if (onboardCompanies && onboardCompanies.length > 0) {
        await onboardCompanies.forEach((co: any) => {
          let cur = availableCompanies.find((c: any) => c.PRCo == parseInt(co));
          if (cur) {
            this.companiesEnabledForOnboarding.push(cur);
          }
        });
        // no companies when comparing available and onboard companies, disable onboarding
        if (!this.companiesEnabledForOnboarding || this.companiesEnabledForOnboarding.length === 0) {
          this.enableOnboarding = false;
        }
      }
      // since there is no companies in the onboarding list, disable onboarding
      else {
        this.enableOnboarding = false;
      }
    }
    else {
      this.companiesEnabledForOnboarding = null;
      this.enableOnboarding = false;
    }
  }

  setLoginOptions() {
    switch (Number(this.portalSettings.SecurityMode.ConfigValue)) { 
      case 1:
        this.isWindowsLoginEnabled = false;
        this.isEmployeeLoginEnabled = true;
        break;
      case 2:
        this.isWindowsLoginEnabled = true;
        this.isEmployeeLoginEnabled = false;
        break;
      default:
        this.isWindowsLoginEnabled = true;
        this.isEmployeeLoginEnabled = true;
        break;
    }

    this.isViewpointLoginEnabled = this.portalSettings.ViewpointOneLoginEnabled.ConfigValue == 1;

    this.getVistaTidEnabled();
  }

  changeMainMenu(menu: MainMenuType) {
    if (this.isCompanySelection && this.isCoDropdownVisible) {
      this.updateCompanyOnMenuSwitch();
    }

    this.syncEmployeeNum(menu);
    if (this.activeMainMenu == MainMenuType.Enroll) {
      this.enrollForm.reset();
    }

    this.activeMainMenu = menu;

    if (this.activeMainMenu == MainMenuType.Default) {
      this.updateInputFocusForSubMenu();
    }
  }

  // Help keep the company dropdown in sync with the company selected in the other forms
  updateCompanyOnMenuSwitch() {
    switch (this.activeMainMenu) {
      case MainMenuType.Default:
        this.company = this.employeeLoginForm.value.companySelect;
        break;
      case MainMenuType.Enroll:
        this.company = this.enrollForm.value.companySelect;
        break;
      case MainMenuType.SignInHelp:
      this.company = this.passwordResetForm.value.companySelect;
        break;
    }

    this.setCompanySelect(this.company);
  }

  changeSubMenu(menu: SubMenuType) {
    this.activeSubMenu = menu;
    this.updateInputFocusForSubMenu();
  }

  viewpointLogin() {
    var viewpointLoginUrl = "/Account/IdentityLogin";
    if (this.returnUrl != null || this.returnUrl != '') {
      viewpointLoginUrl += "?relayUrl=" + this.returnUrl + encodeURIComponent(window.location.hash);
    }
    window.location.href = viewpointLoginUrl;
  }

  employeeLogin(loginType: LoginType = LoginType.Default) {
    if (this.employeeLoginForm.invalid) {
      this.notificationsService.showError("Please enter your login credentials to sign in", window.location.pathname);
      return;
    }

    // Determine where we're getting the company number from
    let company = null;
    if (this.isCompanySelection && !this.coDropdownCompanies) {
      company = this.employeeLoginForm.value.companyInput;
    }
    else if (this.isCompanySelection && this.coDropdownCompanies) {
      company = this.employeeLoginForm.value.companySelect;
    }
    else {
      company = this.company;
    }

    this.isLoading = true;
    let loginData = {
      Employee: this.employeeLoginForm.value.employeeNum,
      Password: this.employeeLoginForm.value.employeePassword,
      PRCo: company,
      PMMode: loginType == LoginType.FieldMode && this.enableFieldMode,
      ViewpointID: this.vpid,
      IdentityToken: this.idToken
    };

    this.accountService.login(loginData).then((response: any) => {
      if (response.success) {
        if (loginType == LoginType.ClockInOut &&  this.enableClockInOut) {
          window.location.href = "/Employee/ClockInOut";
        }
        else {
          if (this.returnUrl) {
            window.location.href = this.returnUrl + window.location.hash;
          }
          else {
            window.location.href = '/';
          }
        }
      } else {
        this.isLoading = false;
        this.notificationsService.showError(this.loginErrorMsg, window.location.pathname);
      }
    }, (error) => {
      this.isLoading = false;
    });
  }

  windowsLogin(loginType: LoginType = LoginType.Default) {
    if (this.windowsLoginForm.invalid) {
      this.notificationsService.showError("Please enter your login credentials to sign in", window.location.pathname);
      return; 
    }

    this.isLoading = true;

    let loginData = {
      username: this.windowsLoginForm.value.windowsUsername,
      password: this.windowsLoginForm.value.windowsPassword,
      loginPIN: this.windowsLoginForm.value.windowsPin,
      pmMode: loginType == LoginType.FieldMode && this.enableFieldMode,
      usingADEmail: this.portalSettings.LoginUsingADEmailField.ConfigValue == 1,
      viewpointID: this.vpid,
      idToken: this.idToken
    };

    this.accountService.windowsLogin(loginData).then((response: any) => {
      if (response.success) {
        if (loginType == LoginType.ClockInOut && this.enableClockInOut) { 
          window.location.href = "/Employee/ClockInOut";
        }
        else if (this.returnUrl){
          window.location.href = this.returnUrl + window.location.hash;
        }
        else {
          window.location.href = '/';
        }
      } else {
        this.isLoading = false;
        this.notificationsService.showError(response.errorMessage, window.location.pathname);
      }
    }, (error) => {
      this.isLoading = false;
      this.notificationsService.showError("An error occurred while logging in. Please try again.", window.location.pathname); 
    });
  }

  goToOnboarding() {
    // If only one company is enabled for onboarding, go directly to that company
    if (this.companiesEnabledForOnboarding.length == 1) {
      window.location.href = "/Careers/Welcome#/C/" + this.companiesEnabledForOnboarding[0].PRCo;
    }
    // Otherwise choose from the list
    else if (this.companiesEnabledForOnboarding.length > 1) {
      // open company picker modal
      let modalRef = this.ngbModalService.open(LoginOnboardCoPickModalComponent, { size: 'md', backdrop: 'static' });
      modalRef.componentInstance.companies = this.companiesEnabledForOnboarding;
      modalRef.result.then((result: any) => {
        if (result) {
          window.location.href = "/Careers/Welcome#/C/" + result;
        }
      }).catch((res: any) => { });
    }
  }

  enroll() {
    if (this.setPasswordInEnrollment && this.doesPasswordsMatch(this.enrollForm.value.password, this.enrollForm.value.confirmPassword) == false) {
      return;
    }
    
    this.isLoading = true;

    // Determine where we're getting the company number from
    let company = null;
    if (this.isCompanySelection && !this.coDropdownCompanies) {
      company = this.enrollForm.value.companyInput;
    }
    else if (this.isCompanySelection && this.coDropdownCompanies) {
      company = this.enrollForm.value.companySelect;
    }
    else {
      company = this.company;
    }

    let accountData = {
      PRCo: company,
      Employee: this.enrollForm.value.employeeNum,
      Password: this.enrollForm.value.password,
      ConfirmPassword: this.enrollForm.value.confirmPassword,
      LastFourSSN: this.enrollForm.value.securityPin
    }

    this.accountService.createAccount(accountData).then((response: any) => {
      if (response.success) {
        if (response.msg == null) {
          if (this.returnUrl)
            window.location.href = this.returnUrl + window.location.hash
          else
            window.location.href = '/';
        }
        else {
          this.notificationsService.showSuccess(response.msg);
          this.isLoading = false;
        }
      }
      else {
        // if not successful, bind errors to error variables
        this.isLoading = false;
        if (response.errorMessage == null) {
          this.notificationsService.showError(response.msg, window.location.pathname);
          if (response.url != null) {
            window.location.href = response.url;
          }
        }
        else {
          this.notificationsService.showError(response.errorMessage, window.location.pathname);
        }
      }
    }, (error) => {
      this.isLoading = false;
    })
  }

  resetPassword() {
    this.isLoading = true;

    // Determine where we're getting the company number from
    let company = null;
    if (this.isCompanySelection && !this.coDropdownCompanies) {
      company = this.passwordResetForm.value.companyInput;
    }
    else if (this.isCompanySelection && this.coDropdownCompanies) {
      company = this.passwordResetForm.value.companySelect;
    }
    else {
      company = this.company;
    }

    this.accountService.requestPasswordReset(this.passwordResetForm.value.employeeNum, company, this.passwordResetForm.value.securityPin).then((response: any) => {
      if (response.success && response.isSent) {
        if (response.isSMSRequest) {
          window.location.href = '/Account/ResetAccountPassword?manualToken=true';
        }
        else {
          this.isLoading = false;
          this.notificationsService.showSuccess(response.responseMsg);
          this.changeMainMenu(MainMenuType.Default);

          // clearing all but the company number dropdowns
          this.employeeLoginForm.controls['employeeNum'].setValue('');
          this.employeeLoginForm.controls['employeePassword'].setValue('');
          this.windowsLoginForm.controls['windowsUsername'].setValue('');
          this.windowsLoginForm.controls['windowsPassword'].setValue('');
          this.enrollForm.controls['employeeNum'].setValue('');
          this.enrollForm.controls['securityPin'].setValue('');
          this.enrollForm.controls['password'].setValue('');
          this.enrollForm.controls['confirmPassword'].setValue('');
          this.passwordResetForm.controls['employeeNum'].setValue('');
          this.passwordResetForm.controls['securityPin'].setValue('');
        }
      }
      else {
        this.isLoading = false;
        this.notificationsService.showError(response.responseMsg, window.location.pathname);
      }
    }, (error) => {
      this.isLoading = false;
    });
  }

  getOnboardingCompanies(): any {
    let enableOnboardLoginByCompany = this.portalSettings.EnableOnboardLoginByCompany.ConfigValue;
    if (enableOnboardLoginByCompany !== null && enableOnboardLoginByCompany !== '' && enableOnboardLoginByCompany !== undefined) {
      return enableOnboardLoginByCompany.split(',');
    }
    else {
      return null;
    }
  }

  setCompanySelect(co: number) {
    this.employeeLoginForm.controls['companySelect'].setValue(co);
    this.enrollForm.controls['companySelect'].setValue(co);
    this.passwordResetForm.controls['companySelect'].setValue(co);
  }

  setCompanyValidationOnForms() {
    if (this.isCompanySelection && this.isCoDropdownVisible) {
      this.employeeLoginForm.get('companySelect').setValidators(Validators.required);
      this.employeeLoginForm.get('companyInput').clearValidators();
      this.enrollForm.get('companySelect').setValidators(Validators.required);
      this.enrollForm.get('companyInput').clearValidators();
      this.passwordResetForm.get('companySelect').setValidators(Validators.required);
      this.passwordResetForm.get('companyInput').clearValidators();
    }
    else if (this.isCompanySelection && !this.isCoDropdownVisible) {
      this.employeeLoginForm.get('companySelect').clearValidators();
      this.employeeLoginForm.get('companyInput').setValidators(Validators.required);
      this.enrollForm.get('companySelect').clearValidators();
      this.enrollForm.get('companyInput').setValidators(Validators.required);
      this.passwordResetForm.get('companySelect').clearValidators();
      this.passwordResetForm.get('companyInput').setValidators(Validators.required);
    }
    else {
      this.employeeLoginForm.get('companySelect').clearValidators();
      this.employeeLoginForm.get('companyInput').clearValidators();
      this.enrollForm.get('companySelect').clearValidators();
      this.enrollForm.get('companyInput').clearValidators();
      this.passwordResetForm.get('companySelect').clearValidators();
      this.passwordResetForm.get('companyInput').clearValidators();
    }

    this.employeeLoginForm.updateValueAndValidity();
    this.enrollForm.updateValueAndValidity();
    this.passwordResetForm.updateValueAndValidity();
  }

  updateCompanyLogo(event: any) {
    if (this.changeLogo) {
      this.setLogoUrl(event.target.value);
    }
  }

  setLogoUrl(co: number, size: string = 'L') {
    this.logoUrl = '/Company/GetLogo?co=' + co + '&size=' + size;
  }

  goToPasswordReset() {
    window.location.href = '/Account/ResetAccountPassword?manualToken=true';
  }

  capsLockEvent(event: KeyboardEvent) {
    this.capsLockOn = event.getModifierState && event.getModifierState('CapsLock');
  }

  windowResizeEvent(event) {
    if (window.innerWidth < 768)
      this.isMobileMode = true;
    else
      this.isMobileMode = false;
  }

  doesPasswordsMatch(password1: string, password2: string): boolean {
    if (password1 != password2) {
      this.notificationsService.showError("Passwords must match!", window.location.pathname);
      return false;
    } else {
      return true;
    }
  }

  syncEmployeeNum(menu: MainMenuType) {
    // Sync employee number between sign in help and employee login forms
    if (this.activeMainMenu == MainMenuType.SignInHelp) {
      this.employeeLoginForm.controls['employeeNum'].setValue(this.passwordResetForm.value.employeeNum);
    }
    else if (menu == MainMenuType.SignInHelp) {
      this.passwordResetForm.controls['employeeNum'].setValue(this.employeeLoginForm.value.employeeNum);
    }
  }

  updateValidatorsFromPortalSettings() {
    // update windows login page to include the pin when it's required.
    if (this.isWindowsPinRequired) {
      this.windowsLoginForm.get('windowsPin').setValidators(Validators.required);
    }
    else {
      this.windowsLoginForm.get('windowsPin').clearValidators();
    }
    this.windowsLoginForm.get('windowsPin').updateValueAndValidity();

    // update enroll form to include password requirements
    if (this.setPasswordInEnrollment) {
      this.enrollForm.get('password').setValidators(Validators.required);
      this.enrollForm.get('confirmPassword').setValidators(Validators.required);
    }
    else {
      this.enrollForm.get('password').clearValidators();
      this.enrollForm.get('confirmPassword').clearValidators();
    }
    this.enrollForm.get('password').updateValueAndValidity();
    this.enrollForm.get('confirmPassword').updateValueAndValidity();
  }

  updateInputFocusForSubMenu() {
    setTimeout(() => {
      if (this.activeSubMenu == SubMenuType.EmployeeLogin) {
        this.employeeLoginNum?.nativeElement?.focus();
      }
      else if (this.activeSubMenu == SubMenuType.WindowsLogin) {
        this.windowsUsername?.nativeElement?.focus();
      }
    }, 100);
  }

  clearForms() {
    this.employeeLoginForm.reset();
    this.windowsLoginForm.reset();
    this.enrollForm.reset();
    this.passwordResetForm.reset();
  }
}
